// src/pages/Rides.js - Added Map View
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ExportButton from '../components/ExportButton';
import DateRangeFilter from '../components/DateRangeFilter';

function Rides() {
  const [rides, setRides] = useState([]);
  const [filteredRides, setFilteredRides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'
  const [selectedRide, setSelectedRide] = useState(null);
  const navigate = useNavigate();

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchRides();
    const interval = setInterval(fetchRides, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    filterRides();
  }, [searchTerm, filter, rides, dateRange]);

  const fetchRides = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/all-rides`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      let ridesData = [];
      if (response.data.rides) {
        ridesData = response.data.rides;
      } else if (Array.isArray(response.data)) {
        ridesData = response.data;
      }
      
      setRides(ridesData);
      setFilteredRides(ridesData);
    } catch (error) {
      console.error('Error fetching rides:', error);
      setError(error.response?.data?.error || 'Failed to load rides');
    } finally {
      setLoading(false);
    }
  };

  const filterRides = () => {
    let filtered = [...rides];
    
    if (filter !== 'all') {
      filtered = filtered.filter(r => r.status === filter);
    }
    
    if (dateRange && dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59);
      filtered = filtered.filter(ride => {
        const date = new Date(ride.created_at);
        return date >= start && date <= end;
      });
    }
    
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(ride => {
        const id = String(ride.id || '');
        const customer = (ride.customer_name || ride.rider_name || '').toLowerCase();
        const driver = (ride.driver_name || '').toLowerCase();
        const pickup = (ride.pickup_address || '').toLowerCase();
        const dropoff = (ride.dropoff_address || '').toLowerCase();
        const status = (ride.status || '').toLowerCase();
        
        return id.includes(term) || 
               customer.includes(term) || 
               driver.includes(term) || 
               pickup.includes(term) || 
               dropoff.includes(term) ||
               status.includes(term);
      });
    }
    
    setFilteredRides(filtered);
  };

  const getStatusBadge = (status) => {
    const colors = {
      completed: { bg: '#dcfce7', color: '#166534', icon: '✅' },
      in_progress: { bg: '#fef3c7', color: '#92400e', icon: '🔄' },
      pending: { bg: '#fef3c7', color: '#92400e', icon: '⏳' },
      cancelled: { bg: '#fee2e2', color: '#991b1b', icon: '❌' },
      accepted: { bg: '#dbeafe', color: '#1e40af', icon: '✓' },
      arrived_at_pickup: { bg: '#dcfce7', color: '#166534', icon: '📍' },
      scheduled: { bg: '#e0e7ff', color: '#4338ca', icon: '📅' },
    };
    const config = colors[status] || { bg: '#f1f5f9', color: '#64748b', icon: '📌' };
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '20px',
        fontSize: '12px',
        backgroundColor: config.bg,
        color: config.color,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
      }}>
        {config.icon} {config.text || status}
      </span>
    );
  };

  const getStatusCount = (status) => {
    if (status === 'all') return rides.length;
    return rides.filter(r => r.status === status).length;
  };

  const goToRideDetails = (rideId) => {
    navigate(`/rides/${rideId}`);
  };

  const getExportData = () => {
    return filteredRides.map(r => ({
      'Ride ID': r.id,
      Customer: r.customer_name || r.rider_name || '—',
      Driver: r.driver_name || '—',
      Fare: r.total_fare || 0,
      Vehicle: r.vehicle_type || '—',
      Status: r.status || '—',
      Date: r.created_at ? new Date(r.created_at).toLocaleDateString() : '—',
    }));
  };

  const searchStats = {
    total: rides.length,
    shown: filteredRides.length,
    hidden: rides.length - filteredRides.length
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading rides...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Rides Management</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#64748b', flexWrap: 'wrap' }}>
            <span>🚗 {rides.length} total</span>
            {searchTerm && (
              <span style={{ color: '#f97316' }}>
                🔍 {filteredRides.length} shown
                {searchStats.hidden > 0 && ` (${searchStats.hidden} hidden)`}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* View Mode Toggle */}
          <div style={{
            display: 'flex',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px',
                backgroundColor: viewMode === 'list' ? '#f97316' : 'white',
                color: viewMode === 'list' ? 'white' : '#64748b',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              📋 List
            </button>
            <button
              onClick={() => setViewMode('map')}
              style={{
                padding: '8px 16px',
                backgroundColor: viewMode === 'map' ? '#f97316' : 'white',
                color: viewMode === 'map' ? 'white' : '#64748b',
                border: 'none',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              🗺️ Map
            </button>
          </div>

          <DateRangeFilter 
            onApply={(range) => setDateRange(range)}
            onClear={() => setDateRange(null)}
            label="Filter by Date"
          />
          
          <ExportButton 
            data={getExportData()} 
            filename="rides_export" 
            label="Export CSV"
          />
          
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Search rides..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 16px',
                paddingRight: searchTerm ? '40px' : '16px',
                borderRadius: '10px',
                border: '2px solid #e2e8f0',
                width: '280px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#f97316'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#94a3b8',
                }}
              >
                ✕
              </button>
            )}
          </div>
          
          <button
            onClick={fetchRides}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '14px 18px',
          borderRadius: '10px',
          marginBottom: '16px',
          border: '1px solid #fecaca',
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Stats Summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
        gap: '10px',
        marginBottom: '16px',
      }}>
        {['all', 'completed', 'in_progress', 'pending', 'cancelled'].map(status => {
          const count = getStatusCount(status);
          const colors = {
            all: { bg: '#f1f5f9', color: '#64748b' },
            completed: { bg: '#dcfce7', color: '#166534' },
            in_progress: { bg: '#fef3c7', color: '#92400e' },
            pending: { bg: '#fef3c7', color: '#92400e' },
            cancelled: { bg: '#fee2e2', color: '#991b1b' },
          };
          const config = colors[status] || colors.all;
          return (
            <div
              key={status}
              onClick={() => setFilter(status)}
              style={{
                backgroundColor: filter === status ? config.bg : 'white',
                padding: '12px',
                borderRadius: '8px',
                textAlign: 'center',
                border: filter === status ? `2px solid ${config.color}` : '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: config.color }}>
                {count}
              </div>
              <div style={{ fontSize: '11px', color: '#64748b', textTransform: 'capitalize' }}>
                {status === 'all' ? 'Total' : status.replace('_', ' ')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Map View */}
      {viewMode === 'map' && (
        <div style={{
          backgroundColor: '#f0f2f5',
          borderRadius: '12px',
          height: '400px',
          position: 'relative',
          overflow: 'hidden',
          marginBottom: '16px',
          border: '1px solid #e2e8f0',
        }}>
          {/* Map Background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#dce1e8',
          }}>
            {/* Grid Pattern */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `
                linear-gradient(rgba(148,163,184,0.2) 1px, transparent 1px),
                linear-gradient(90deg, rgba(148,163,184,0.2) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
            }} />
            
            {/* Road Network */}
            <div style={{ position: 'absolute', top: '15%', left: '5%', right: '5%', height: '2px', backgroundColor: 'rgba(200,200,200,0.4)' }} />
            <div style={{ position: 'absolute', top: '45%', left: '8%', right: '8%', height: '2px', backgroundColor: 'rgba(200,200,200,0.4)' }} />
            <div style={{ position: 'absolute', top: '70%', left: '3%', right: '3%', height: '2px', backgroundColor: 'rgba(200,200,200,0.4)' }} />
            <div style={{ position: 'absolute', top: '5%', bottom: '5%', left: '25%', width: '2px', backgroundColor: 'rgba(200,200,200,0.4)' }} />
            <div style={{ position: 'absolute', top: '10%', bottom: '10%', left: '55%', width: '2px', backgroundColor: 'rgba(200,200,200,0.4)' }} />
            <div style={{ position: 'absolute', top: '8%', bottom: '8%', left: '80%', width: '2px', backgroundColor: 'rgba(200,200,200,0.4)' }} />
          </div>

          {/* Map Label */}
          <div style={{
            position: 'absolute',
            top: '14px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(255,255,255,0.92)',
            padding: '6px 20px',
            borderRadius: '20px',
            fontSize: '13px',
            color: '#475569',
            zIndex: 10,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            fontWeight: '500',
          }}>
            🗺️ {filteredRides.length} rides • Click marker for details
          </div>

          {/* Ride Markers */}
          {filteredRides.slice(0, 30).map((ride, index) => {
            // Generate random-looking positions for demo
            const x = 5 + ((index * 37 + 13) % 90);
            const y = 8 + ((index * 53 + 7) % 84);
            const statusColors = {
              completed: '#10b981',
              in_progress: '#f59e0b',
              pending: '#f59e0b',
              cancelled: '#ef4444',
              accepted: '#3b82f6',
            };
            const color = statusColors[ride.status] || '#94a3b8';
            
            return (
              <div
                key={ride.id}
                onClick={() => {
                  setSelectedRide(selectedRide?.id === ride.id ? null : ride);
                  if (ride.id) goToRideDetails(ride.id);
                }}
                style={{
                  position: 'absolute',
                  left: `${x}%`,
                  top: `${y}%`,
                  transform: 'translate(-50%, -50%)',
                  cursor: 'pointer',
                  zIndex: selectedRide?.id === ride.id ? 20 : 5,
                  transition: 'transform 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)'}
              >
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  border: selectedRide?.id === ride.id ? '3px solid #f97316' : '2px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                }}>
                  #{ride.id}
                </div>
                <div style={{
                  fontSize: '9px',
                  textAlign: 'center',
                  marginTop: '2px',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  padding: '2px 6px',
                  borderRadius: '8px',
                  whiteSpace: 'nowrap',
                  maxWidth: '60px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {ride.customer_name || `Ride ${ride.id}`}
                </div>
              </div>
            );
          })}

          {/* Legend */}
          <div style={{
            position: 'absolute',
            bottom: '16px',
            left: '16px',
            backgroundColor: 'rgba(255,255,255,0.95)',
            padding: '10px 14px',
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            fontSize: '11px',
            zIndex: 10,
          }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Status</div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }} />
                Completed
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#f59e0b' }} />
                In Progress
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                Cancelled
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#3b82f6' }} />
                Accepted
              </span>
            </div>
          </div>

          {/* Ride Count */}
          <div style={{
            position: 'absolute',
            bottom: '16px',
            right: '16px',
            backgroundColor: 'rgba(255,255,255,0.95)',
            padding: '6px 14px',
            borderRadius: '20px',
            fontSize: '12px',
            color: '#64748b',
            zIndex: 10,
          }}>
            {filteredRides.length} rides shown
          </div>
        </div>
      )}

      {/* Rides Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'auto',
      }}>
        {filteredRides.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <p style={{ color: '#64748b', fontSize: '16px' }}>
              {searchTerm ? `No rides found matching "${searchTerm}"` : 'No rides found'}
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginTop: '8px',
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Ride ID</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Customer</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Driver</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Fare</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Vehicle</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Date</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRides.map((ride) => (
                <tr key={ride.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px', fontWeight: '500' }}>#{ride.id}</td>
                  <td style={{ padding: '12px' }}>
                    {ride.customer_name || ride.rider_name || 
                      (ride.rider_id ? `User ${ride.rider_id}` : '—')}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {ride.driver_name || (ride.driver_id ? `Driver ${ride.driver_id}` : '—')}
                  </td>
                  <td style={{ padding: '12px', fontWeight: 'bold', color: '#f97316' }}>
                    UGX {ride.total_fare?.toLocaleString() || 0}
                  </td>
                  <td style={{ padding: '12px' }}>
                    {ride.vehicle_type === 'boda' ? '🛵' : 
                     ride.vehicle_type === 'boda_electric' ? '⚡' :
                     ride.vehicle_type === 'car' ? '🚗' : 
                     ride.vehicle_type === 'car_xl' ? '🚙' : '—'}
                  </td>
                  <td style={{ padding: '12px' }}>{getStatusBadge(ride.status)}</td>
                  <td style={{ padding: '12px', fontSize: '12px', color: '#64748b' }}>
                    {ride.created_at ? new Date(ride.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <button
                      onClick={() => goToRideDetails(ride.id)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                      }}
                    >
                      👁️ View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
        {filteredRides.length > 0 && (
          <div style={{ 
            padding: '12px', 
            color: '#64748b', 
            fontSize: '13px',
            borderTop: '1px solid #e2e8f0',
            marginTop: '12px',
          }}>
            Showing {filteredRides.length} of {rides.length} rides
          </div>
        )}
      </div>
    </div>
  );
}

export default Rides;